import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Space,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PositionDetailOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { formatDateTime } from '@/utils/format';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';

type Props = {
  record: PositionDetailOut;
  publication?: PublicationListOut;
};

export const PositionGeneralTab = ({ record, publication }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'publisher_id',
      label: t('progress.positions.fields.publisher_id'),
      children: orDash(record.publisher_id),
    },
    {
      key: 'account_id',
      label: t('progress.positions.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('progress.positions.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'publication_id',
      label: t('progress.positions.fields.publication_id'),
      children: orDash(
        record.publication_id != null && (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        )
      ),
    },
    {
      key: 'device_id',
      label: t('progress.positions.fields.device_id'),
      children: orDash(
        record.device_id && (
          <Typography.Text code>{record.device_id}</Typography.Text>
        )
      ),
    },
  ];

  const productItems: DescriptionsProps['items'] = [
    {
      key: 'product_id',
      label: t('progress.positions.fields.product_id'),
      children: orDash(
        record.product_id != null && (
          <Space size={4}>
            {record.product_id}
            {record.publication_id != null && (
              <PublicationDrawerIcon publicationId={record.publication_id} />
            )}
          </Space>
        )
      ),
    },
    {
      key: 'product_version',
      label: t('progress.positions.fields.product_version'),
      children: orDash(record.product_version),
    },
    {
      key: 'product_kind',
      label: t('progress.positions.fields.product_kind'),
      children: (
        <ProductFormatTag value={record.product_kind} labels="positions" />
      ),
    },
    {
      key: 'product_variant',
      label: t('progress.positions.fields.product_variant'),
      children: (
        <ProductVariantTag value={record.product_variant} labels="positions" />
      ),
    },
  ];

  const timeItems: DescriptionsProps['items'] = [
    {
      key: 'client_timestamp',
      label: t('progress.positions.fields.client_timestamp'),
      children: orDash(formatDateTime(record.client_timestamp)),
    },
    {
      key: 'synced_at',
      label: t('progress.positions.fields.synced_at'),
      children: orDash(formatDateTime(record.synced_at)),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('progress.positions.sections.product')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={productItems}
            />
          </Card>
          <Card title={t('base.sections.system_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={timeItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          <Card title={t('progress.positions.sections.locator')}>
            {/* Opaque JSONB whose shape differs per product kind — shown raw,
                scrolling inside the card rather than stretching the layout. */}
            <pre
              style={{
                margin: 0,
                maxHeight: 360,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: token.fontSizeSM,
              }}
            >
              {JSON.stringify(record.locator, null, 2)}
            </pre>
          </Card>
        </Flex>
      </Col>
    </Row>
  );
};
